import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { IoArrowBack } from "react-icons/io5";
import { Button, Input, Text, Title } from "rizzui";
import { IVoucher } from "../../utilities/types/Voucher";
import {
  EDiscountType,
  EPromotionStatus,
  IPromotionDTO,
} from "@/utilities/types/Promotion";
import { formatDuration } from "@/utilities/functions";
import PromotionRepository from "@/utilities/repositories/Promotion";

interface CheckoutFormData {
  promoCode: string;
}

interface CheckoutProps {
  planName: string;
  price: number;
  monthlyPrice: number;
  onBack: () => void;
  onNext?: (appliedPromotion?: IPromotionDTO) => void;
  duration?: number; // days
}

export default function Checkout({
  planName,
  price,
  monthlyPrice,
  onBack,
  onNext,
  duration,
}: CheckoutProps) {
  const [isPromoValid, setIsPromoValid] = React.useState(false);
  const [isCheckingPromo, setIsCheckingPromo] = React.useState(false);
  const [appliedPromotion, setAppliedPromotion] =
    React.useState<IPromotionDTO | null>(null);

  const { register, watch, setValue } = useForm<CheckoutFormData>();

  const promoCode = watch("promoCode");

  const handlePaymentComplete = () => {
    onNext?.(appliedPromotion || undefined);
  };

  const handleNext = () => {
    handlePaymentComplete();
  };

  const applyPromotion = async () => {
    if (!promoCode) {
      toast.error("Please enter a promo code");
      return;
    }

    setIsCheckingPromo(true);

    try {
      const response = await PromotionRepository.getPromotionByCode(promoCode);

      if (response.data.status === EPromotionStatus.DEACTIVE) {
        toast.error("This promotion has already been used");
        return;
      }
      if (response.data) {
        const voucher = response.data;

        toast.success("Promotion applied successfully!");
        setIsPromoValid(true);
        setAppliedPromotion(voucher);
      } else {
        toast.error("Invalid promotion code");
        setIsPromoValid(false);
        setAppliedPromotion(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to validate promotion code");
      setIsPromoValid(false);
      setAppliedPromotion(null);
    } finally {
      setIsCheckingPromo(false);
    }
  };

  //const timer = useCountdown(appliedPromotion?.heldAt);

  // const revertVoucher = async () => {
  //   if (!appliedPromotion) {
  //     toast.error("No voucher applied");
  //     return;
  //   }

  //   try {
  //     const response = await VoucherRepository.revertVoucher(
  //       appliedPromotion.code
  //     );

  //     if (response.data) {
  //       toast.success("Voucher reverted successfully!");
  //       setIsPromoValid(false);
  //       setValue("promoCode", "");
  //       setAppliedPromotion(null);
  //       timer?.resetCountDown();
  //     }
  //   } catch (error) {
  //     console.error(error);
  //     toast.error("Failed to revert voucher");
  //   }
  // };

  // Calculate the final price after voucher application
  // const calculateFinalPrice = () => {
  //   if (!isPromoValid || !appliedPromotion?.promotion) return price;

  //   const { promotion } = appliedPromotion;

  //   if (
  //     promotion.discountType === EDiscountType.PERCENT &&
  //     promotion.discount
  //   ) {
  //     const discountAmount = (price * promotion.discount) / 100;
  //     return Math.max(0, price - discountAmount);
  //   }

  //   if (promotion.discountType === EDiscountType.PRICE && promotion.discount) {
  //     return Math.max(0, price - promotion.discount);
  //   }

  //   return price;
  // };

  // const finalPrice = calculateFinalPrice();

  // useEffect(() => {
  //   if (isCheckingPromo) return;

  //   // Only run the revert action if timer exists and has reached zero
  //   if (appliedPromotion?.heldAt && appliedPromotion.heldAt < new Date()) {
  //     revertVoucher();
  //   }
  // }, [appliedPromotion, isCheckingPromo]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Button
        variant="outline"
        className="mb-6 inline-flex items-center gap-2"
        onClick={onBack}
      >
        <IoArrowBack className="text-lg" />
        Back to Plans
      </Button>

      <div className="mb-8 rounded-lg bg-white p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
        <Title className="mb-6 text-2xl font-bold">Order Summary</Title>
        <div className="flex justify-between border-b pb-4">
          <div>
            <Text className="text-lg font-semibold">{planName}</Text>
            {duration && (
              <Text className="text-gray-600">{formatDuration(duration)}</Text>
            )}
          </div>
          <div className="text-right">
            <Text
              className={`text-2xl font-bold ${isPromoValid ? "line-through text-gray-400" : ""}`}
            >
              ${Number(price).toFixed(0)}
            </Text>
            {/* {isPromoValid && (
              <Text className="text-2xl font-bold text-green-600">
                ${Number(finalPrice).toFixed(0)}
              </Text>
            )} */}
          </div>
        </div>

        <div className="mt-6">
          <div className="mb-4 flex items-center gap-2">
            <Input
              type="text"
              placeholder="Enter promotion code"
              {...register("promoCode")}
              className="max-w-xs"
              disabled={isPromoValid}
            />
            <Button
              variant="solid"
              onClick={applyPromotion}
              isLoading={isCheckingPromo}
              disabled={isCheckingPromo || !promoCode}
            >
              Apply
            </Button>
            {/* {!isPromoValid ? (
              <Button
                variant="solid"
                onClick={applyPromotion}
                isLoading={isCheckingPromo}
                disabled={isCheckingPromo || !promoCode}
              >
                Apply
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={revertVoucher}
                className="text-red-500 hover:bg-red-50"
              >
                Remove
              </Button>
            )} */}
          </div>
          {/* {timer && appliedPromotion && (
            <div className="my-2">
              <CountdownClock timer={timer} />
            </div>
          )} */}

          {appliedPromotion && (
            <Text className="text-sm text-green-600">
              {appliedPromotion?.code} applied successfully!
            </Text>
          )}
        </div>

        <Button
          variant="solid"
          size="lg"
          className="mt-6 w-full"
          onClick={handleNext}
        >
          Confirm Order
        </Button>
      </div>
    </div>
  );
}
